import { supabase } from '@/integrations/supabase/client';

export interface TransactionFeatures {
  description: string;
  merchant_name?: string;
  amount: number;
  transaction_date: string;
  location_id?: string;
}

export interface ClassificationResult {
  category_type: 'revenue' | 'expenses' | 'balance_sheet' | null;
  subcategory: string | null;
  detail_source: string | null;
  confidence: number;
  method: 'ml' | 'historical' | 'none';
  similarTransactions?: Array<{
    id: string;
    description: string;
    similarity: number;
  }>;
}

/**
 * Normalizes merchant name for better matching
 */
function normalizeMerchantName(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^\w\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b(inc|llc|ltd|corp|corporation|company|co)\b/g, '')
    .trim();
}

/**
 * Calculates text similarity using multiple methods
 */
function calculateTextSimilarity(text1: string, text2: string): number {
  const t1 = text1.toLowerCase().trim();
  const t2 = text2.toLowerCase().trim();
  
  if (t1 === t2) return 1.0;
  if (t1.length === 0 || t2.length === 0) return 0;
  
  // Jaccard similarity (word-based)
  const words1 = new Set(t1.split(/\s+/));
  const words2 = new Set(t2.split(/\s+/));
  const intersection = new Set([...words1].filter(x => words2.has(x)));
  const union = new Set([...words1, ...words2]);
  const jaccard = intersection.size / union.size;
  
  // Substring similarity
  const longer = t1.length > t2.length ? t1 : t2;
  const shorter = t1.length > t2.length ? t2 : t1;
  const substringSim = longer.includes(shorter) ? shorter.length / longer.length : 0;
  
  // Levenshtein-based similarity
  const maxLen = Math.max(t1.length, t2.length);
  if (maxLen === 0) return 1.0;
  
  // Simple Levenshtein approximation
  let distance = 0;
  const minLen = Math.min(t1.length, t2.length);
  for (let i = 0; i < minLen; i++) {
    if (t1[i] !== t2[i]) distance++;
  }
  distance += Math.abs(t1.length - t2.length);
  const levenshteinSim = 1 - (distance / maxLen);
  
  // Weighted combination
  return (jaccard * 0.4 + substringSim * 0.3 + levenshteinSim * 0.3);
}

/**
 * Finds similar labeled transactions using similarity matching
 */
async function findSimilarTransactions(
  features: TransactionFeatures,
  companyId: string,
  accountId?: string,
  limit: number = 5
): Promise<Array<{
  id: string;
  transaction_date: string;
  amount: number;
  description: string;
  merchant_name?: string;
  category_type: string;
  subcategory: string;
  detail_source?: string;
  similarity: number;
}>> {
  // Load labeled transactions (those with category_type set)
  let query = supabase
    .from('bank_transactions')
    .select('id, transaction_date, amount, description, merchant_name, category_type, subcategory, detail_source')
    .eq('company_id', companyId)
    .not('category_type', 'is', null)
    .order('transaction_date', { ascending: false })
    .limit(1000); // Load recent transactions for comparison
  
  if (accountId) {
    query = query.eq('account_id', accountId);
  }
  
  const { data: transactions, error } = await query;
  if (error || !transactions || transactions.length === 0) {
    return [];
  }
  
  // Calculate similarity for each transaction
  const similarities = transactions.map(tx => {
    let similarity = 0;
    let factors = 0;
    
    // Description similarity
    if (features.description && tx.description) {
      const descSim = calculateTextSimilarity(features.description, tx.description);
      similarity += descSim * 0.5;
      factors += 0.5;
    }
    
    // Merchant name similarity
    if (features.merchant_name && tx.merchant_name) {
      const merchant1 = normalizeMerchantName(features.merchant_name);
      const merchant2 = normalizeMerchantName(tx.merchant_name);
      const merchantSim = calculateTextSimilarity(merchant1, merchant2);
      similarity += merchantSim * 0.4;
      factors += 0.4;
    }
    
    // Amount similarity (±20% range)
    if (features.amount && tx.amount) {
      const amount1 = Math.abs(features.amount);
      const amount2 = Math.abs(tx.amount);
      const amountDiff = Math.abs(amount1 - amount2) / Math.max(amount1, amount2);
      const amountSim = amountDiff <= 0.2 ? 1 - (amountDiff / 0.2) : 0;
      similarity += amountSim * 0.1;
      factors += 0.1;
    }
    
    // Normalize by factors
    const finalSimilarity = factors > 0 ? similarity / factors : 0;
    
    return {
      ...tx,
      similarity: finalSimilarity,
    };
  });
  
  // Sort by similarity and return top matches
  return similarities
    .filter(tx => tx.similarity > 0.5) // Only return reasonably similar transactions
    .sort((a, b) => b.similarity - a.similarity)
    .slice(0, limit);
}

/**
 * Classifies a transaction using ML/similarity-based approach
 */
export async function classifyTransactionML(
  features: TransactionFeatures,
  companyId: string,
  accountId?: string
): Promise<ClassificationResult> {
  // Find similar transactions
  const similarTransactions = await findSimilarTransactions(features, companyId, accountId, 5);
  
  if (similarTransactions.length === 0) {
    return {
      category_type: null,
      subcategory: null,
      detail_source: null,
      confidence: 0,
      method: 'none',
    };
  }
  
  // Group by classification and calculate weighted confidence
  const classifications = new Map<string, {
    category_type: string;
    subcategory: string;
    detail_source?: string;
    totalSimilarity: number;
    count: number;
  }>();
  
  for (const similar of similarTransactions) {
    const key = `${similar.category_type}:${similar.subcategory}:${similar.detail_source || ''}`;
    const existing = classifications.get(key);
    
    if (existing) {
      existing.totalSimilarity += similar.similarity;
      existing.count += 1;
    } else {
      classifications.set(key, {
        category_type: similar.category_type,
        subcategory: similar.subcategory,
        detail_source: similar.detail_source || undefined,
        totalSimilarity: similar.similarity,
        count: 1,
      });
    }
  }
  
  // Find the best classification (highest weighted similarity)
  let bestClassification: {
    category_type: string;
    subcategory: string;
    detail_source?: string;
    confidence: number;
  } | null = null;
  let bestScore = 0;
  
  for (const [key, classification] of classifications.entries()) {
    // Weight by both similarity and count (more examples = more confidence)
    const avgSimilarity = classification.totalSimilarity / classification.count;
    const countBonus = Math.min(classification.count / 5, 1) * 0.2; // Up to 20% bonus for multiple examples
    const score = avgSimilarity * 0.8 + countBonus;
    
    if (score > bestScore) {
      bestScore = score;
      bestClassification = {
        category_type: classification.category_type,
        subcategory: classification.subcategory,
        detail_source: classification.detail_source,
        confidence: Math.min(score, 0.95),
      };
    }
  }
  
  if (!bestClassification || bestScore < 0.6) {
    return {
      category_type: null,
      subcategory: null,
      detail_source: null,
      confidence: bestScore,
      method: 'historical',
      similarTransactions: similarTransactions.slice(0, 3).map(tx => ({
        id: tx.id,
        description: tx.description,
        similarity: tx.similarity,
      })),
    };
  }
  
  return {
    category_type: bestClassification.category_type as 'revenue' | 'expenses' | 'balance_sheet',
    subcategory: bestClassification.subcategory,
    detail_source: bestClassification.detail_source || null,
    confidence: bestClassification.confidence,
    method: 'ml',
    similarTransactions: similarTransactions.slice(0, 3).map(tx => ({
      id: tx.id,
      description: tx.description,
      similarity: tx.similarity,
    })),
  };
}

/**
 * Batch classifies multiple transactions
 */
export async function classifyTransactionsML(
  transactions: Array<TransactionFeatures & { id: string; company_id: string; account_id?: string }>,
  companyId: string,
  accountId?: string
): Promise<Map<string, ClassificationResult>> {
  const results = new Map<string, ClassificationResult>();
  
  // Process in batches to avoid overwhelming the database
  const batchSize = 10;
  for (let i = 0; i < transactions.length; i += batchSize) {
    const batch = transactions.slice(i, i + batchSize);
    const batchResults = await Promise.all(
      batch.map(tx => classifyTransactionML(
        {
          description: tx.description,
          merchant_name: tx.merchant_name,
          amount: tx.amount,
          transaction_date: tx.transaction_date,
          location_id: tx.account_id,
        },
        tx.company_id,
        tx.account_id
      ))
    );
    
    batch.forEach((tx, idx) => {
      results.set(tx.id, batchResults[idx]);
    });
  }
  
  return results;
}

